"use client"
import { Stack, Theme, Toolbar, styled, useMediaQuery } from "@mui/material"
import { useIdle } from "@tc/ui-dfe/hooks/useIdle"
import { XmlRequestType } from "@tc/ui-dfe/query"
import { signOutCleanup } from "@tc/ui-dfe/utils/helper"
import { signOut } from "next-auth/react"
import { useEffect, useRef, useState } from "react"
import { XmlSubmitModal } from "../XmlSubmitModal"
import { Sidebar } from "./Sidebar"
import { TopNavbar } from "./TopNavbar"

export interface LayoutProps {
  children: React.ReactNode
  logoutUrl: string
}
const Main = styled("div", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean
}>(({ theme, open }) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  position: "relative",
  [theme.breakpoints.up("lg")]: {
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  },
}))
const minutes = 60
export const Layout = ({ children, logoutUrl }: LayoutProps) => {
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"))
  const xlUp = useMediaQuery("(min-width:1440px)")
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))
  const { isIdle } = useIdle(60 * minutes)
  const [open, setOpen] = useState(() => true)
  const [xmlModalOpen, setXmlModalOpen] = useState(false)
  const [userClosed, setUserClosed] = useState(false)
  const mountRef = useRef(false)

  const handleXmlSuccess = (
    responseId?: string,
    requestType?: XmlRequestType,
  ) => {
    if (responseId && requestType) {
      const requestTypePath =
        requestType === XmlRequestType.Transfer ? "transfer" : "export"
      window.location.href = `/request/${requestTypePath}/view/${responseId}?submitted=true`
    }
  }

  useEffect(() => {
    if (mountRef.current === false) {
      lgUp ? setOpen(true) : setOpen(false)
      mountRef.current = true
      return
    }
    // Auto-open when screen goes above 1440px, unless user explicitly closed it
    if (xlUp && !userClosed) {
      setOpen(true)
    }
  }, [lgUp, xlUp, userClosed])

  useEffect(() => {
    const autoLogout = () => {
      localStorage.removeItem("timeout")
      signOutCleanup()
      signOut({
        callbackUrl: logoutUrl,
      })
    }
    if (isIdle) {
      autoLogout()
    }
  }, [isIdle, logoutUrl])
  return (
    <Stack
      direction={"row"}
      flexGrow={1}
      id="layout"
      component={"main"}
      role="main"
    >
      <TopNavbar
        color="primary"
        open={open}
        onOpen={() => {
          setOpen(true)
          setUserClosed(false)
        }}
        logoutUrl={logoutUrl}
      />
      <Sidebar
        open={open}
        onClose={() => {
          setOpen(false)
          setUserClosed(true)
        }}
        logoutUrl={logoutUrl}
        onModalOpen={() => setXmlModalOpen(true)}
      />
      <Main key={"main-wapper-key"} id="main-wapper" open={open}>
        <Toolbar />
        {mdDown && (
          <Toolbar
            data-testid="testToolbarMdDown"
            sx={{
              minHeight: {
                xs: 48,
              },
            }}
          />
        )}
        {children}
      </Main>
      <XmlSubmitModal
        open={xmlModalOpen}
        onClose={() => setXmlModalOpen(false)}
        onSuccess={handleXmlSuccess}
      />
    </Stack>
  )
}
